/**
 * Authentication Context
 * Provides auth state and methods throughout the app
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import * as authService from '../services/authService';
import { clearTokens } from '../services/tokenRefreshInterceptor';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load user on mount if token exists
  useEffect(() => {
    async function loadUser() {
      if (authService.isAuthenticated()) {
        try {
          const { user } = await authService.getCurrentUser();
          setUser(user);
        } catch (err) {
          console.error('Failed to load user:', err);
          if (err?.status === 401) {
            clearTokens();
          }
        }
      }
      setLoading(false);
    }

    loadUser();
  }, []);

  // Listen for auth events from api.js and authService.js
  useEffect(() => {
    const handleLogout = (_event) => {
      console.log('[AuthContext] Logout event received:', event.detail?.reason);
      setUser(null);
    };

    const handleTokenRefresh = async () => {
      console.log('[AuthContext] Token refresh event received, reloading user...');
      try {
        const { user } = await authService.getCurrentUser();
        setUser(user);
      } catch (err) {
        console.error('[AuthContext] Failed to reload user after token refresh:', err);
        setUser(null);
      }
    };

    window.addEventListener('auth:logout', handleLogout);
    window.addEventListener('auth:token-refreshed', handleTokenRefresh);

    return () => {
      window.removeEventListener('auth:logout', handleLogout);
      window.removeEventListener('auth:token-refreshed', handleTokenRefresh);
    };
  }, []);

  // Periodic token health check (every 5 minutes)
  useEffect(() => {
    if (!user) {return;}

    const healthCheckInterval = setInterval(async () => {
      try {
        // Validate token is still valid by fetching current user
        await authService.getCurrentUser();
      } catch (err) {
        console.error('[AuthContext] Token health check failed, logging out:', err);
        // Token expired or invalid - logout user
        await logout();
      }
    }, 15 * 60 * 1000); // 15 minutes

    return () => clearInterval(healthCheckInterval);
  }, [user]);

  /**
   * Register a new user
   */
  const register = async (userData) => {
    try {
      setError(null);
      const response = await authService.register(userData);
      setUser(response.user);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  /**
   * Login with email and password
   */
  const login = async (email, password, rememberMe = false) => {
    try {
      setError(null);
      const response = await authService.login(email, password, rememberMe);
      setUser(response.user);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  /**
   * Login with Google OAuth
   */
  const loginWithGoogle = async (credential, userType = 'customer') => {
    try {
      setError(null);
      const response = await authService.loginWithGoogle(credential, userType);
      setUser(response.user);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  /**
   * Login with Facebook OAuth
   */
  const loginWithFacebook = async (accessToken, userType = 'customer') => {
    try {
      setError(null);
      const response = await authService.loginWithFacebook(accessToken, userType);
      setUser(response.user);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  /**
   * Logout current user
   */
  const logout = async () => {
    try {
      await authService.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
    }
  };

  /**
   * Refresh access token
   */
  const refreshToken = async () => {
    try {
      const response = await authService.refreshAccessToken();
      return response;
    } catch (err) {
      // If refresh fails, logout user
      await logout();
      throw err;
    }
  };

  /**
   * Change password
   */
  const changePassword = async (currentPassword, newPassword) => {
    try {
      setError(null);
      await authService.changePassword(currentPassword, newPassword);
      // Force logout after password change
      await logout();
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  /**
   * Store token from federated auth callback and load user.
   * Called by AuthCallbackPage after Cognito/SAML redirect (SCRUM-1778).
   */
  const setTokenFromCallback = async (token) => {
    localStorage.setItem('accessToken', token);
    localStorage.setItem('tokenIssuedAt', String(Date.now()));

    try {
      const { user: callbackUser } = await authService.getCurrentUser();
      setUser(callbackUser);
    } catch (err) {
      if (err?.status === 401) {
        clearTokens();
      }
    }
  };

  /**
   * Check if user has specific role
   */
  const hasRole = (roleName) => {
    if (!user) {return false;}
    return (Array.isArray(user.roles) && user.roles.includes(roleName))
        || user.primaryRole === roleName;
  };

  // SCRUM-1616: Expose accessToken so WebSocket hooks (useNotificationSocket) can
  // authenticate the Socket.io handshake. Derived from localStorage — stays in sync
  // because this component re-renders whenever user state changes (login/logout/refresh).
  const accessToken = user ? localStorage.getItem('accessToken') : null;

  const value = {
    user,
    accessToken,
    loading,
    error,
    isAuthenticated: !!user,
    register,
    login,
    loginWithGoogle,
    loginWithFacebook,
    logout,
    refreshToken,
    changePassword,
    hasRole,
    setTokenFromCallback,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to use auth context
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
